import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Typography, Paper, Box, TextField, Button, Grid, Checkbox, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import styled, { createGlobalStyle } from 'styled-components';

function formatReal(value) {
  if (value === '' || value === undefined) return '';
  let num = value;
  if (typeof num === 'string') {
    num = num.replace(/\D/g, '');
    if (num === '') return '';
    num = parseFloat(num);
  }
  return (num / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateBR(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

const terracota = '#8a3929ff';
const terracotaDark = '#A0522D';
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    font-family: 'Segoe UI', Arial, sans-serif;
    background: #fff7f3;
    color: ${terracotaDark};
  }
`;


const TitleBox = styled.div`
  background: ${terracota};
  border-radius: 24px;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem auto 2rem auto;
  max-width: 600px;
`;

const Title = styled.h1`
  font-family: 'Dancing Script', cursive;
  font-size: 2.5rem;
  color: #fff;
  text-align: center;
  margin: 0;
`;

const Container1 = styled.div`
  max-width: 900px;
  margin: auto;
  padding: 2rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(109, 36, 21, 0.15);
`;

const Button1 = styled.button`
  background: ${terracota};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1.2rem;
  font-size: 1rem;
  cursor: pointer;
  margin: 0.5rem;
  &:hover {
    background: ${terracotaDark};
  }
`;


const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: bold;
  margin-right: 0.5rem;
  min-width: 120px;
`;

const Input = styled.input`
  padding: 0.4rem;
  border-radius: 6px;
  border: 1px solid #ccc;
  margin-right: 1rem;
  width: 100%;
  max-width: 180px;
  box-sizing: border-box;
`;

const ParcelaBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Card = styled.div`
  background: #fff7f3;
  border: 1px solid ${terracota};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const Table = styled.table`
  width: 100%;
  max-width: 500px;
  margin: 1rem auto;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(109, 36, 21, 0.1);
`;

const Th = styled.th`
  background: ${terracota};
  color: #fff;
  padding: 0.75rem;
  text-align: left;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
`;

const Tfoot = styled.tfoot`
  font-weight: bold;
  background: #f7f7f7;
`;

function DespesaItem({ despesa, parcelas, isEditing, onEdit, onCancel, onUpdate, onDelete, onCheckParcela }) {
  const [form, setForm] = useState({ nome: '', valor_total: '', parcelas: 1, parcelasInfo: [] });

  useEffect(() => {
    if (isEditing) {
      setForm({
        nome: despesa.nome,
        valor_total: despesa.valor_total,
        parcelas: despesa.parcelas,
        parcelasInfo: parcelas || []
      });
    }
  }, [isEditing, despesa, parcelas]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'valor_total') {
      let raw = value.replace(/\D/g, '');
      if (raw.length < 3) raw = raw.padStart(3, '0');
      setForm(f => ({ ...f, valor_total: raw }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    if (name === 'parcelas') {
      const n = parseInt(value) || 1;
      setForm(f => ({ ...f, parcelasInfo: Array(n).fill().map((_, i) => f.parcelasInfo[i] || { numero: i+1, valor: '', vencimento: '', paga: false }) }));
    }
  };

  const handleParcelaChange = (i, field, value) => {
    setForm(f => {
      const parcelasInfo = [...f.parcelasInfo];
      if (field === 'valor') {
        let raw = value.replace(/\D/g, '');
        if (raw.length < 3) raw = raw.padStart(3, '0');
        parcelasInfo[i][field] = raw;
      } else {
        parcelasInfo[i][field] = field === 'paga' ? value : value;
      }
      return { ...f, parcelasInfo };
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    onUpdate(despesa.id, form);
  };

  if (isEditing) {
    return (
      <Card>
        <form onSubmit={handleUpdate}>
          <FormRow>
            <Label>Nome da Despesa</Label>
            <Input name="nome" value={form.nome} onChange={handleFormChange} required />
            <Label>Valor Total</Label>
            <Input
              name="valor_total"
              type="text"
              inputMode="numeric"
              value={formatReal(form.valor_total)}
              onChange={handleFormChange}
              required
            />
            <Label>Nº de Parcelas</Label>
            <Input name="parcelas" type="number" min={1} value={form.parcelas} onChange={handleFormChange} required />
          </FormRow>
          <div>
            {form.parcelasInfo.map((p, i) => (
              <FormRow key={i}>
                <span>Parcela {i+1}</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Valor"
                  value={formatReal(p.valor)}
                  onChange={e => handleParcelaChange(i, 'valor', e.target.value)}
                  required
                />
                <Input type="date" value={p.vencimento} onChange={e => handleParcelaChange(i, 'vencimento', e.target.value)} required />
                <Label>Pago</Label>
                <Input type="checkbox" checked={!!p.paga} onChange={e => handleParcelaChange(i, 'paga', e.target.checked)} />
              </FormRow>
            ))}
          </div>
          <Button type="submit">Salvar</Button>
          <Button type="button" onClick={onCancel}>Cancelar</Button>
        </form>
      </Card>
    );
  }

  return (
    <Card>
      <strong>{despesa.nome}</strong> <br />
      Valor Total: {formatReal(despesa.valor_total)} <br />
      Nº Parcelas: {despesa.parcelas}
      <div style={{marginTop: '0.5rem'}}>
        <strong>Parcelas:</strong>
        <div>
          {parcelas && parcelas.map((p, i) => (
            <ParcelaBox key={i}>
              <span>Parcela {p.numero}</span>
              <ParcelaInfo parcela={p} onCheck={onCheckParcela} />
            </ParcelaBox>
          ))}
        </div>
      </div>
      <Button onClick={() => onEdit(despesa.id)}>Editar</Button>
      <Button onClick={() => onDelete(despesa.id)}>Excluir</Button>
    </Card>
  );
}

function OrcamentoDetalhado() {
  const [despesas, setDespesas] = useState([]);
  const [parcelas, setParcelas] = useState({});
  const [form, setForm] = useState({ nome: '', valor_total: '', parcelas: 1, parcelasInfo: [] });
  const [editId, setEditId] = useState(null);
  const [somaParcelas, setSomaParcelas] = useState(0);
  const [somaMensal, setSomaMensal] = useState([]);

  useEffect(() => {
    fetchDespesas();
  }, []);

  const fetchDespesas = async () => {
    const res = await axios.get('/api/despesas');
    const despesasData = res.data;
    setDespesas(despesasData);

    const promises = despesasData.map(d => fetchParcelas(d.id));
    const parcelasResults = await Promise.all(promises);

    const parcelasMap = {};
    despesasData.forEach((d, index) => {
      parcelasMap[d.id] = parcelasResults[index];
    });
    setParcelas(parcelasMap);
  };

  const fetchParcelas = async (despesaId) => {
    const res = await axios.get(`/api/parcelas/${despesaId}`);
    return res.data;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'valor_total') {
      let raw = value.replace(/\D/g, '');
      if (raw.length < 3) raw = raw.padStart(3, '0');
      setForm(f => ({ ...f, valor_total: raw }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    if (name === 'parcelas') {
      const n = parseInt(value) || 1;
      setForm(f => ({ ...f, parcelasInfo: Array(n).fill().map((_, i) => f.parcelasInfo[i] || { numero: i+1, valor: '', vencimento: '', paga: false }) }));
    }
  };

  const handleParcelaChange = (i, field, value) => {
    setForm(f => {
      const parcelasInfo = [...f.parcelasInfo];
      if (field === 'valor') {
        // Aceita apenas números e formata para centavos
        let raw = value.replace(/\D/g, '');
        if (raw.length < 3) raw = raw.padStart(3, '0');
        parcelasInfo[i][field] = raw;
      } else {
        parcelasInfo[i][field] = field === 'paga' ? value : value;
      }
      return { ...f, parcelasInfo };
    });
  };

  // Soma automática das parcelas
  useEffect(() => {
    const soma = form.parcelasInfo.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
    setSomaParcelas(soma);
  }, [form.parcelasInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post('/api/despesas', form);
    setForm({ nome: '', valor_total: '', parcelas: 1, parcelasInfo: [] });
    fetchDespesas();
  };

  const handleUpdateDespesa = async (id, formData) => {
    await axios.put(`/api/despesas/${id}`, formData);
    setEditId(null);
    fetchDespesas();
  };

  const handleEdit = (id) => {
    setEditId(id);
  };

  const handleCancelEdit = () => {
    setEditId(null);
  };

  const handleDelete = async (id) => {
    await axios.delete(`/api/despesas/${id}`);
    fetchDespesas();
  };

  const handleCheckParcela = async (parcela) => {
    await axios.put(`/api/parcelas/${parcela.id}/paga`, { paga: !parcela.paga });
    fetchDespesas();
  };

  // Totais
  const [totais, setTotais] = useState({
    valor_total: 0,
    valor_parcelas: 0,
    valor_parcelas_pagas: 0,
    valor_parcelas_nao_pagas: 0
  });

  useEffect(() => {
    let valor_total = 0, valor_parcelas = 0, valor_parcelas_pagas = 0, valor_parcelas_nao_pagas = 0;
    const mensal = {};

    for (const d of despesas) {
      valor_total += Number(d.valor_total);
      if (parcelas[d.id]) {
        parcelas[d.id].forEach(p => {
          valor_parcelas += Number(p.valor);
          if (p.paga) {
            valor_parcelas_pagas += Number(p.valor);
          } else {
            valor_parcelas_nao_pagas += Number(p.valor);
            if (p.vencimento) {
              const [year, month] = p.vencimento.split('-');
              const key = `${year}-${month}`;
              if (!mensal[key]) {
                mensal[key] = 0;
              }
              mensal[key] += Number(p.valor);
            }
          }
        });
      }
    }

    const sortedMensal = Object.keys(mensal)
      .sort()
      .map(key => ({
        mes: key,
        valor: mensal[key]
      }));

    setSomaMensal(sortedMensal);
    setTotais({ valor_total, valor_parcelas, valor_parcelas_pagas, valor_parcelas_nao_pagas });
  }, [despesas, parcelas]);

  return (
    <Container>
        <h2>Adicionar Nova Despesa</h2>
        <form onSubmit={handleSubmit}>
          <FormRow>
            <Label>Nome da Despesa</Label>
            <Input name="nome" value={form.nome} onChange={handleFormChange} required />
            <Label>Valor Total</Label>
            <Input
              name="valor_total"
              type="text"
              inputMode="numeric"
              value={formatReal(form.valor_total)}
              onChange={handleFormChange}
              required
            />
            <Label>Nº de Parcelas</Label>
            <Input name="parcelas" type="number" min={1} value={form.parcelas} onChange={handleFormChange} required />
          </FormRow>
          <div>
            {form.parcelasInfo.map((p, i) => (
              <FormRow key={i}>
                <span>Parcela {i+1}</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Valor"
                  value={formatReal(p.valor)}
                  onChange={e => handleParcelaChange(i, 'valor', e.target.value)}
                  required
                />
                <Input type="date" value={p.vencimento} onChange={e => handleParcelaChange(i, 'vencimento', e.target.value)} required />
                <Label>Pago</Label>
                <Input type="checkbox" checked={!!p.paga} onChange={e => handleParcelaChange(i, 'paga', e.target.checked)} />
              </FormRow>
            ))}
            {form.parcelasInfo.length > 0 && (
              <div style={{margin: '0.5rem 0', fontWeight: 'bold', color: '#A0522D'}}>Soma das parcelas: {formatReal(somaParcelas)}</div>
            )}
          </div>
          <Button type="submit">Adicionar Despesa</Button>
        </form>
        <hr />
        <h2>Despesas Lançadas</h2>
        {despesas.map(d => (
          <DespesaItem
            key={d.id}
            despesa={d}
            parcelas={parcelas[d.id]}
            isEditing={editId === d.id}
            onEdit={handleEdit}
            onCancel={handleCancelEdit}
            onUpdate={handleUpdateDespesa}
            onDelete={handleDelete}
            onCheckParcela={handleCheckParcela}
          />
        ))}
        <hr />
        <h2>Totais</h2>
        <Table>
          <tbody>
            <tr>
              <Td>Valor Total</Td>
              <Td>{formatReal(totais.valor_total)}</Td>
            </tr>
            <tr>
              <Td>Valor Parcelas</Td>
              <Td>{formatReal(totais.valor_parcelas)}</Td>
            </tr>
            <tr>
              <Td>Parcelas Pagas</Td>
              <Td>{formatReal(totais.valor_parcelas_pagas)}</Td>
            </tr>
            <tr>
              <Td>Parcelas Não Pagas</Td>
              <Td>{formatReal(totais.valor_parcelas_nao_pagas)}</Td>
            </tr>
          </tbody>
        </Table>
        <hr />
        <h2>Soma das parcelas não pagas por mês</h2>
        <Table>
          <thead>
            <tr>
              <Th>Mês</Th>
              <Th>Valor</Th>
            </tr>
          </thead>
          <tbody>
            {somaMensal.map(item => (
              <tr key={item.mes}>
                <Td>{formatMonthYear(item.mes)}</Td>
                <Td>{formatReal(item.valor)}</Td>
              </tr>
            ))}
          </tbody>
          <Tfoot>
            <tr>
              <Td>Total</Td>
              <Td>{formatReal(somaMensal.reduce((acc, item) => acc + item.valor, 0))}</Td>
            </tr>
          </Tfoot>
        </Table>
      </Container>
  );
};

function ParcelaInfo({ parcela, onCheck }) {
  if (!parcela) return null;
  return (
    <>
      <span>{formatReal(parcela.valor)}</span>
      <span>{formatDateBR(parcela.vencimento)}</span>
      <Input type="checkbox" checked={!!parcela.paga} onChange={() => onCheck(parcela)} />
    </>
  );
}

function formatMonthYear(dateStr) {
  const [year, month] = dateStr.split('-');
  const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];
  return `${monthNames[parseInt(month) - 1]} de ${year}`;
}

export default OrcamentoDetalhado;
